import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { GET_PAYMENT_LINES_WITH_SALES_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext';
import {
  PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
  PAYMENT_SINGLE_MONTH_QUERY_KEY,
} from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { GET_PAYMENT_LINES_WITH_SALES_BY_FILTERS_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/useGetMarketplacePaymentLinesWithSalesByFilters';
import { useConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog/useConfirmDialog';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import {
  EditMarketplacePaymentLineDialog,
  EditMarketplacePaymentLineRefActions,
} from 'src/dialogs/EditMarketplacePaymentLineDialog.tsx/EditMarketplacePaymentLineDialog';
import { LinkMarketplacePaymentToSaleDialog } from 'src/dialogs/LinkMarketplacePaymentToSaleDialog';
import { UnlinkMarketplacePaymentLineToSaleDialog } from 'src/dialogs/UnlinkMarketplacePaymentLineDialog/UnlinkMarketplacePaymentLineToSaleDialog';
import { MarketplacePaymentLineWithSaleAndEventInfo } from 'src/hooks/useGetMarketplacePaymentLineTableData';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CheckIcon, IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import {
  LinkMarketplacePaymentLineRefActions,
  LinkMarketplacePaymentLineToSale,
} from 'src/tables/MarketplacePaymentLinesTable/Cell/LinkMarketplacePaymentLineToSale';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  MarketplacePaymentClient,
  MarketplacePaymentLineType,
  MarketplacePaymentSaleLineInput,
  SaleLineItem,
} from 'src/WebApiController';

export function MarketplacePaymentLinesActionsCell({
  marketplacePaymentLine,
}: {
  marketplacePaymentLine: MarketplacePaymentLineWithSaleAndEventInfo;
}) {
  const linkActionsRef = useRef<LinkMarketplacePaymentLineRefActions | null>(
    null
  );
  const unlinkActionsRef = useRef<LinkMarketplacePaymentLineRefActions | null>(
    null
  );

  const editLineActionsRef =
    useRef<EditMarketplacePaymentLineRefActions | null>(null);

  const { activeAccountWebClientConfig } = useAppContext();
  const queryClient = useQueryClient();

  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [GET_PAYMENT_LINES_WITH_SALES_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [GET_PAYMENT_LINES_WITH_SALES_BY_FILTERS_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [
        PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
        activeAccountWebClientConfig.activeAccountId,
      ],
    });
    await queryClient.invalidateQueries({
      queryKey: [PAYMENT_SINGLE_MONTH_QUERY_KEY, true],
      refetchType: 'active',
    });
  }, [activeAccountWebClientConfig.activeAccountId, queryClient]);

  const actionsMsg = useContent(ContentId.Actions);
  const addedToSaleMsg = useContent(ContentId.AddedToSale);
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog } = useClientLogContext();

  const userHasLinkProceedWithSaleLineItemFeature = useUserHasFeature(
    Feature.LinkProceedWithSaleLineItem
  );

  const canLinkPaymentLine = !marketplacePaymentLine.saleId;

  const canUnlinkMarketplaceLine = !!marketplacePaymentLine.saleId;

  const canManuallyAddToSale = userHasLinkProceedWithSaleLineItemFeature
    ? marketplacePaymentLine.saleId != null
    : marketplacePaymentLine.saleId != null &&
      (marketplacePaymentLine.type === MarketplacePaymentLineType.Credit ||
        marketplacePaymentLine.type === MarketplacePaymentLineType.Charge);

  const canEditMarketplacePaymentLine = !!marketplacePaymentLine.saleId;

  const isAddedToSale = marketplacePaymentLine?.isAddedToSale ?? false;

  const {
    openConfirmDialog: openRemoveFromSaleDialog,
    closeConfirmDialog: closeRemoveFromSaleDialog,
  } = useConfirmDialog();
  const {
    openConfirmDialog: openConfirmDeleteDialog,
    closeConfirmDialog: closeConfirmDeleteDialog,
  } = useConfirmDialog();

  const { openDialog: openAddToSaleDialog, closeDialog: closeAddToSaleDialog } =
    useDialog(
      DialogId.AddMarketplacePaymentLineToSale,
      LinkMarketplacePaymentToSaleDialog
    );

  const marketplacePaymentLineId = marketplacePaymentLine.id;

  // Handle Edit submission
  const handleEditSubmit = useCallback(
    async (data: MarketplacePaymentSaleLineInput) => {
      setIsLoading(true);
      await tryInvokeApi(
        async () => {
          await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).updateMarketplacePaymentLine({ ...data });
          await refreshData();
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.updateMarketplacePaymentLine',
            error,
            {
              trackErrorData: { data },
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [activeAccountWebClientConfig, refreshData, showErrorDialog]
  );

  const onRemoveFromSale = useCallback(() => {
    closeRemoveFromSaleDialog();

    setIsLoading(true);
    tryInvokeApi(
      async () => {
        await new MarketplacePaymentClient(
          activeAccountWebClientConfig
        ).removeMarketplacePaymentLineFromSaleLineItems(
          marketplacePaymentLineId!
        );

        refreshData();
      },
      (error) => {
        showErrorDialog(
          'MarketplacePaymentClient.removeMarketplacePaymentLineFromSaleLineItems',
          error,
          {
            trackErrorData: { marketplacePaymentLineId },
          }
        );
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    closeRemoveFromSaleDialog,
    marketplacePaymentLineId,
    refreshData,
    showErrorDialog,
  ]);

  const onSubmitAddToSale = useCallback(
    (marketplacePaymentLineId: number, saleLineItems: SaleLineItem[]) => {
      closeAddToSaleDialog();
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).addMarketplacePaymentLineToSaleLineItems(
            marketplacePaymentLineId,
            saleLineItems
          );
          refreshData();
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.addMarketplacePaymentLineToSaleLineItems',
            error,
            {
              trackErrorData: { marketplacePaymentLineId },
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      closeAddToSaleDialog,
      refreshData,
      showErrorDialog,
    ]
  );

  const dropdownItems: ReactNode[] = [];

  if (canManuallyAddToSale) {
    dropdownItems.push(
      <>
        {isAddedToSale ? (
          <PosDropdownItem
            key="RemoveFromSale"
            disabled={!!marketplacePaymentLine.isLocked}
            onClick={() =>
              openRemoveFromSaleDialog({
                size: 'm',
                headerText: <Content id={ContentId.RemoveFromSale} />,
                bodyText: <Content id={ContentId.AreYouSure} />,
                onOkay: onRemoveFromSale,
                onCancel: closeRemoveFromSaleDialog,
                okText: ContentId.Yes,
                cancelText: ContentId.No,
              })
            }
          >
            <Stack alignItems="end" gap="m" width="full">
              <Content id={ContentId.RemoveFromSale} />
            </Stack>
          </PosDropdownItem>
        ) : (
          <PosDropdownItem
            key="AddToSale"
            disabled={!!marketplacePaymentLine.isLocked}
            onClick={() => {
              openAddToSaleDialog({
                marketplacePaymentLine: {
                  ...marketplacePaymentLine,
                  lineExtPmtId: null,
                  pmtRefId: null,
                },
                onSubmit: onSubmitAddToSale,
                onClosed: () => {
                  setIsLoading(false);
                },
                onCancel: closeAddToSaleDialog,
                hasLinkProceedWithSaleLineItemFeature:
                  userHasLinkProceedWithSaleLineItemFeature,
              });
            }}
          >
            <Stack alignItems="end" gap="m" width="full">
              <Content id={ContentId.AddToSale} />
            </Stack>
          </PosDropdownItem>
        )}
      </>
    );
  }

  if (canLinkPaymentLine) {
    dropdownItems.push(
      <PosDropdownItem
        key="link-payment-line"
        disabled={!!marketplacePaymentLine.isLocked}
        onClick={() => linkActionsRef.current?.showDialog()}
      >
        <Content id={ContentId.LinkSale} />
      </PosDropdownItem>
    );
  } else if (canUnlinkMarketplaceLine) {
    dropdownItems.push(
      <PosDropdownItem
        key="unlink-payment-line"
        disabled={!!marketplacePaymentLine.isLocked}
        onClick={() => unlinkActionsRef.current?.showDialog()}
      >
        <Content id={ContentId.UnlinkSale} />
      </PosDropdownItem>
    );
  }

  if (canEditMarketplacePaymentLine) {
    dropdownItems.push(
      <PosDropdownItem
        key="edit-payment-line"
        disabled={!!marketplacePaymentLine.isLocked}
        onClick={() => editLineActionsRef.current?.showDialog()}
      >
        <Content id={ContentId.EditLine} />
      </PosDropdownItem>
    );
  }

  dropdownItems.push(
    <PosDropdownItem
      key="delete-payment-line"
      disabled={!!marketplacePaymentLine.isLocked}
      onClick={() =>
        openConfirmDeleteDialog({
          size: 's',
          headerText: <Content id={ContentId.DeletePurchasePaymentMethod} />,
          bodyText: <Content id={ContentId.AreYouSure} />,
          onOkay: () => {
            closeConfirmDeleteDialog();
            tryInvokeApi(
              async () => {
                await new MarketplacePaymentClient(
                  activeAccountWebClientConfig
                ).deleteMarketplacePaymentLine(marketplacePaymentLineId!);
                refreshData();
              },
              (error) => {
                showErrorDialog(
                  'MarketplacePaymentClient.deleteMarketplacePaymentLine',
                  error,
                  {
                    trackErrorData: { marketplacePaymentLineId },
                  }
                );
              },
              () => {
                setIsLoading(false);
              }
            );
          },
          onCancel: closeConfirmDeleteDialog,
          okText: ContentId.Yes,
          cancelText: ContentId.No,
        })
      }
    >
      <Content id={ContentId.DeleteLine} />
    </PosDropdownItem>
  );

  return (
    <Stack direction="row" gap="m" alignItems="center">
      {isAddedToSale && (
        <CheckIcon
          size={vars.iconSize.s}
          fill={IconsFill.textBrand}
          title={addedToSaleMsg}
        />
      )}
      {dropdownItems.length > 0 && (
        <PosDropdown
          key={`mkp-pmt-line-action-dropdown-${marketplacePaymentLineId}`}
          trigger={
            <div
              title={actionsMsg}
              style={{
                padding: `0 ${vars.spacing.sm} 0 ${vars.spacing.xxs}`,
              }}
            >
              <MoreIcon
                withHoverEffect
                fill={IconsFill.textBrand}
                align="middle"
                disabled={isLoading}
              />
            </div>
          }
          align="end"
        >
          {...dropdownItems}
        </PosDropdown>
      )}
      {canLinkPaymentLine && (
        <LinkMarketplacePaymentLineToSale
          refActions={linkActionsRef}
          marketplacePaymentLineId={marketplacePaymentLine.id}
          onLinkSuccess={refreshData}
        />
      )}
      {canUnlinkMarketplaceLine && (
        <UnlinkMarketplacePaymentLineToSaleDialog
          refActions={unlinkActionsRef}
          marketplacePaymentLineId={marketplacePaymentLine.id}
          onUnlinkSuccess={refreshData}
        />
      )}
      {canEditMarketplacePaymentLine && (
        <EditMarketplacePaymentLineDialog
          refActions={editLineActionsRef}
          paymentLineData={marketplacePaymentLine}
          marketplacePaymentId={marketplacePaymentLine.posPmtId}
          onSubmit={handleEditSubmit}
        />
      )}
    </Stack>
  );
}
